<template>
    <v-container fluid grid-list-xl class="pt-4 account-lookup-container">
        <v-layout row wrap>
            <v-flex xs12 sm10 md8 lg6 xl5>
                <v-card class="account-lookup-card elevation-3">
                    <div class="account-lookup-header">
                        <v-icon color="white" size="32" class="mr-3">mdi-account-search</v-icon>
                        <div>
                            <div class="account-lookup-title">Account Lookup</div>
                            <div class="account-lookup-subtitle">
                                Search by name, account ID, phone number, or address
                            </div>
                        </div>
                    </div>

                    <v-card-text class="pa-4">
                        <v-text-field
                            v-model="search"
                            label="Search name, account ID, phone, or address"
                            placeholder="Start typing to search..."
                            prepend-icon="mdi-magnify"
                            autofocus
                            clearable
                            solo
                            flat
                            hide-details
                            class="account-lookup-input"
                            @input="onSearch"
                            @click:clear="onClear"
                            @keyup.enter.native="onEnter"
                        ></v-text-field>

                        <div v-if="loading" class="account-lookup-status mt-4">
                            <v-progress-circular
                                indeterminate
                                size="20"
                                width="2"
                                color="#4caf50"
                                class="mr-2"
                            ></v-progress-circular>
                            <span class="grey--text">Searching accounts...</span>
                        </div>

                        <div
                            v-else-if="searched && !results.length"
                            class="account-lookup-status mt-4"
                        >
                            <v-icon color="grey" class="mr-2">mdi-account-off-outline</v-icon>
                            <span class="grey--text">No matching accounts found</span>
                        </div>

                        <div v-else-if="results.length" class="mt-4">
                            <div class="caption grey--text mb-2">
                                {{ results.length }} result{{ results.length === 1 ? "" : "s" }}
                                &mdash; click an account to open it
                            </div>

                            <v-card
                                v-for="account in results"
                                :key="account.accountID"
                                class="account-lookup-result-card mb-2"
                                @click.native="goToAccount(account)"
                            >
                                <v-layout row wrap align-center class="pa-3">
                                    <v-icon
                                        color="#90a4ae"
                                        size="40"
                                        class="mr-3 account-lookup-result-icon"
                                    >
                                        mdi-account-circle
                                    </v-icon>
                                    <v-flex class="account-lookup-result-body">
                                        <div class="subheading font-weight-medium">
                                            {{ account.name || "\u2014" }}
                                            <span
                                                v-if="account.hname"
                                                class="grey--text font-weight-regular"
                                            >
                                                &middot; {{ account.hname }}
                                            </span>
                                        </div>
                                        <div class="caption mt-1">
                                            <span class="font-weight-medium">
                                                Account {{ account.accountID }}
                                            </span>
                                            <span v-if="account.homePhone">
                                                &middot; {{ formatPhone(account.homePhone) }}
                                            </span>
                                            <span v-if="account.cellPhone">
                                                &middot; {{ formatPhone(account.cellPhone) }}
                                            </span>
                                        </div>
                                        <div v-if="account.address1" class="caption grey--text">
                                            {{ account.address1 }}
                                        </div>
                                        <div
                                            v-if="account.emailAddress"
                                            class="caption grey--text"
                                        >
                                            {{ account.emailAddress }}
                                        </div>
                                    </v-flex>
                                    <v-icon color="#b0bec5">mdi-chevron-right</v-icon>
                                </v-layout>
                            </v-card>
                        </div>

                        <div v-else class="account-lookup-empty mt-5 text-xs-center">
                            <v-icon size="64" color="#cfd8dc">mdi-account-search-outline</v-icon>
                            <div class="mt-2 grey--text">
                                Type at least 2 characters to search accounts
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>
<script>
import { formatPhoneNumber } from "../utils/helpers.js";

export default {
    name: "AccountLookup",
    data() {
        return {
            search: "",
            loading: false,
            searched: false,
            results: [],
            searchTimer: null,
        };
    },
    methods: {
        onSearch() {
            this.searched = false;

            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            const value = (this.search || "").trim();
            if (value.length < 2) {
                this.results = [];
                return;
            }

            this.searchTimer = setTimeout(() => {
                this.loadResults(value);
            }, 300);
        },
        onEnter() {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            const value = (this.search || "").trim();
            if (value.length < 2) {
                return;
            }

            this.loadResults(value);
        },
        onClear() {
            this.results = [];
            this.searched = false;
        },
        async loadResults(value) {
            this.loading = true;

            try {
                const results = await this.$http.get(
                    "api/accounts/searchAccounts?value=" + encodeURIComponent(value)
                );
                this.results = results || [];
            } catch (e) {
                this.results = [];
            }

            this.loading = false;
            this.searched = true;

            const digitsOnly = value.replace(/\D/g, "");
            if (digitsOnly.length >= 10 && this.results.length === 1) {
                this.goToAccount(this.results[0]);
            }
        },
        goToAccount(account) {
            if (!account || !account.accountID) {
                return;
            }

            this.$router.push({
                name: "Account View",
                params: { id: account.accountID },
            });
        },
        formatPhone(value) {
            return value ? formatPhoneNumber(value) : "";
        },
    },
};
</script>
<style>
.account-lookup-container {
    background: #f4f6f8;
}

.account-lookup-card {
    border-radius: 10px;
    overflow: hidden;
}

.account-lookup-header {
    background-color: #4caf50;
    padding: 20px 24px;
    display: flex;
    align-items: center;
}

.account-lookup-title {
    color: white;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.2;
}

.account-lookup-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin-top: 2px;
}

.account-lookup-input {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.account-lookup-input .v-input__control {
    min-height: 52px;
}

.account-lookup-status {
    display: flex;
    align-items: center;
    font-size: 14px;
}

.account-lookup-result-card {
    cursor: pointer;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    transition: border-color 0.15s ease, background 0.15s ease,
        box-shadow 0.15s ease;
}

.account-lookup-result-card:hover {
    border-color: #4caf50;
    background: #f7fdf7;
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.18);
}

.account-lookup-result-icon {
    flex: 0 0 auto;
}

.account-lookup-result-body {
    min-width: 0;
}

.account-lookup-empty {
    padding: 24px 0;
}
</style>
